import './UsedByModal.scss';

import React, { useEffect, useRef } from 'react';
import { FlightButton, getIcon } from '@flybits/design-system';
import LoadingIcon from 'components/Shared/LoadingIcon/LoadingIcon';
import { EntityRef } from 'interface/content/content.interface';
import { ReactComponent as IconUsedBy } from 'assets/icons/used-by.svg';
import { ReactComponent as IconLightbulb } from 'assets/icons/lightbulb.svg';

const CLASS_MAIN = 'used-by-modal';
const CLASSES = {
  CHILDREN: `${CLASS_MAIN}__children`,
  CLOSE_ICON: `${CLASS_MAIN}__children__close-icon`,
  BODY: `${CLASS_MAIN}__children__body`,
  ICON: `${CLASS_MAIN}__children__body__icon`,
  CONTAINER: `${CLASS_MAIN}__children__body__container`,
  INFO: `${CLASS_MAIN}__children__body__container__info`,
  TITLE: `${CLASS_MAIN}__children__body__container__info__title`,
  DESCRIPTION: `${CLASS_MAIN}__children__body__container__info__description`,
  LIST: `${CLASS_MAIN}__children__body__container__list`,
  LIST_ITEM: `${CLASS_MAIN}__children__body__container__list__item`,
  BANNER: `${CLASS_MAIN}__children__body__container__banner`,
  INFO_BANNER: `${CLASS_MAIN}__children__body__container__banner--info`,
  BANNER_ICON: `${CLASS_MAIN}__children__body__container__banner__icon`,
  BANNER_TEXT: `${CLASS_MAIN}__children__body__container__banner__text`,
  BANNER_TITLE: `${CLASS_MAIN}__children__body__container__banner__text__title`,
  BANNER_DESC: `${CLASS_MAIN}__children__body__container__banner__text__desc`,
  ACTIONS: `${CLASS_MAIN}__children__body__container__actions`,
  ACTIONS_BUTTON: `${CLASS_MAIN}__children__body__container__actions__button`,
  LOADING_CONTAINER: `${CLASS_MAIN}__loading-container`,
  LOADING_SPINNER: `${CLASS_MAIN}__loading-container__spinner`,
};

type TUsedByModalProps = {
  isOpen: boolean;
  closeModal: () => void;
  title?: string;
  description?: string;
  warningMessage?: string;
  infoMessage?: string;
  referencedBy?: EntityRef[];
  primaryAction: {
    label: string;
    onClickHandler: () => void;
  };
  secondaryAction: {
    label: string;
    onClickHandler: () => void;
  };
  isLoading?: boolean;
  className?: string;
};

const UsedByModal: React.FC<TUsedByModalProps> = ({
  isOpen,
  closeModal,
  title = 'Content is being shared',
  description = 'This content is currently live in:',
  primaryAction,
  secondaryAction,
  isLoading,
  className = '',
  warningMessage = 'The content in this experience is shared with another one. Any edits made here will also impact the other experience.',
  infoMessage = 'Suggestion: duplicate this content in the content manager to avoid affecting live experiences.',
  referencedBy = [],
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (isOpen) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [isOpen]);

  return (
    <dialog ref={dialogRef} onCancel={closeModal} className={`${CLASS_MAIN} ${className}`}>
      {isLoading && (
        <div className={CLASSES.LOADING_CONTAINER}>
          <div className={CLASSES.LOADING_SPINNER}>
            <LoadingIcon />
          </div>
        </div>
      )}
      <div className={CLASSES.CHILDREN}>
        <div className={CLASSES.CLOSE_ICON}>
          <button onClick={closeModal} aria-label="Close modal">
            {getIcon('clear', {})}
          </button>
        </div>
        <div className={CLASSES.BODY}>
          <div className={`${CLASSES.ICON}`}>
            <IconUsedBy />
          </div>
          <div className={CLASSES.CONTAINER}>
            <div className={CLASSES.INFO}>
              <div className={CLASSES.TITLE}>{title}</div>
            </div>
            {warningMessage && (
              <div className={`${CLASSES.BANNER}`}>
                <div className={CLASSES.BANNER_ICON}>{getIcon('warning', {})}</div>
                <div className={CLASSES.BANNER_TEXT}>
                  <div className={CLASSES.BANNER_TITLE}>Warning</div>
                  <div className={CLASSES.BANNER_DESC}>{warningMessage}</div>
                </div>
              </div>
            )}
            <div className={`${CLASSES.DESCRIPTION}`}>{description}</div>
            <ul className={CLASSES.LIST}>
              {referencedBy.map((item) => (
                <li key={item.id} className={CLASSES.LIST_ITEM}>
                  {item.name}
                </li>
              ))}
            </ul>
            {infoMessage && (
              <div className={`${CLASSES.BANNER} ${CLASSES.INFO_BANNER}`}>
                <div className={CLASSES.BANNER_ICON}>
                  <IconLightbulb />
                </div>
                <div className={CLASSES.BANNER_TEXT}>
                  <div className={CLASSES.BANNER_DESC}>{infoMessage}</div>
                </div>
              </div>
            )}
            <div className={`${CLASSES.ACTIONS}`}>
              <div className={`${CLASSES.ACTIONS_BUTTON} ${CLASSES.ACTIONS_BUTTON}--secondary`}>
                <FlightButton
                  label={secondaryAction.label}
                  ariaLabel={secondaryAction.label}
                  onClick={secondaryAction.onClickHandler}
                  theme={'minor'}
                />
              </div>
              <div className={`${CLASSES.ACTIONS_BUTTON}`}>
                <FlightButton
                  label={primaryAction.label}
                  ariaLabel={primaryAction.label}
                  onClick={primaryAction.onClickHandler}
                  theme={'primary'}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </dialog>
  );
};

export default UsedByModal;
